import React from 'react';
import PropTypes from 'prop-types';

import { IClassNames, makeClassNames } from '../../../utils/styles';
import t from '../../../labels/labelling-utils';

import './table-head.scss';

// COLUMNS is an ARRAY of STRINGS, where each COLUMN is the KEY in LABELS.TS {KEY: VALUE} pair
export interface ITableHead {
  columns: string[];
  colspans?: number[];
  className?: string;
  theadClassName?: string;
  trClassName?: string;
  thClassNames?: string[];
  isVisible?: boolean;
}

const classNames = (className: string, isVisible: boolean): IClassNames => {
  return makeClassNames({
    table: [className, !isVisible && `${className}--hidden`],
    row: `${className}__row`,
    cell: `${className}__cell`,
  });
};

/**
 * Results Table HEAD for SEO metadata
 * (it's always hidden by default)
 * @param {String[]} columns - Columns labels
 * @param {Number[]} colspans - Column colspan value
 * @param {String} className - Class to apply to <thead> tag
 * @param {String} theadClassName - Inherited class to apply to <thead> tag
 * @param {String} trClassName - Inherited class to apply to <tr> tag
 * @param {String[]} thClassNames - Inherited classes to apply to each <th> tag
 * @param {Boolean} isVisible - Define if table-head should be visible or not
 */
const TableHead: React.FC<ITableHead> = ({
  columns,
  colspans = [],
  className = 'table-head',
  theadClassName = '',
  trClassName = '',
  thClassNames = [],
  isVisible = false,
}) => {
  const cn = classNames(className, isVisible);

  const getColumnClassName = (key: number) => (thClassNames.length > key ? thClassNames[key] : '');

  return (
    <thead className={`${cn.table} ${theadClassName}`}>
      <tr className={`${cn.row} ${trClassName}`}>
        {columns.map((column: string, key: number) => (
          <th
            className={`${cn.cell} ${cn.cell}--w${String(key + 1).padStart(2, '0')} ${getColumnClassName(key)}`}
            key={key}
            colSpan={colspans && colspans[key] ? colspans[key] : 1}
          >
            {t(column)}
          </th>
        ))}
      </tr>
    </thead>
  );
};

TableHead.propTypes = {
  className: PropTypes.string,
  colspans: PropTypes.array,
  columns: PropTypes.array.isRequired,
  isVisible: PropTypes.bool,
  thClassNames: PropTypes.array,
  theadClassName: PropTypes.string,
  trClassName: PropTypes.string,
};

export default TableHead;
